import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import useSeo from '@/hooks/useSeo';
import { siteConfig } from '@/mocks/site';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于洞洞杂货铺漫画内容站',
  description: siteConfig.description,
};

export default function About() {
  useSeo({
    title: '关于本站 - 洞洞杂货铺漫画内容站',
    description: '了解洞洞杂货铺漫画内容站的定位、发展理念与内容原则。我们专注为读者梳理《洞洞杂货铺》的世界观与故事背景设定。',
    keywords: '洞洞杂货铺漫画,洞洞杂货铺设定,关于洞洞杂货铺',
    ogType: 'website',
    jsonLd,
  });

  return (
    <Layout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-16">
          <nav aria-label="面包屑" className="text-sm text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-700">首页</Link>
            <span className="mx-2 text-foreground-300">/</span>
            <span className="text-foreground-700">关于本站</span>
          </nav>
          <h1 className="mt-4 text-2xl leading-relaxed text-foreground-950 md:text-3xl">关于本站</h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-foreground-600">
            读懂每一个空缺，也读懂光照进来的地方。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-16">
        <div className="grid gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="text-2xl text-foreground-950">我们是谁</h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">
              洞洞杂货铺漫画内容站，是一群喜爱《洞洞杂货铺》这部漫画的读者自发建立的知识内容站。我们相信，一部好的作品值得被认真读懂，也值得被更多人看见。
            </p>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">
              我们专注于将漫画中碎片化呈现的世界观、故事背景、角色线索与隐藏细节，整理成清晰、系统、易于理解的文字，帮助每一位新老读者更快建立对这部作品的完整认知。
            </p>

            <h2 className="mt-12 text-2xl text-foreground-950">我们的定位</h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">
              我们既不是官方，也非资讯媒体，而是一座面向读者的“设定科普站”。无论你想搞懂“洞洞杂货铺漫画是什么”、梳理世界观与故事背景，还是深挖“洞洞”二字的深层含义，这里都是你停靠的驿站。
            </p>

            <h2 className="mt-12 text-2xl text-foreground-950">我们的理念</h2>
            <div className="mt-6 space-y-5">
              {[
                {
                  icon: 'ri-sun-line',
                  title: '温和解读，不剧透伤害',
                  desc: '尽量在保留阅读惊喜的前提下梳理设定，重要的主线悬念会特别标注提醒。',
                },
                {
                  icon: 'ri-lightbulb-line',
                  title: '尊重原作，忠于细节',
                  desc: '所有解读都尽量贴近原作表达，不臆造设定，不夸大解读，以原文细节为依托。',
                },
                {
                  icon: 'ri-community-line',
                  title: '共建共享，持续成长',
                  desc: '欢迎读者提出补充与指正，让内容站随作品一起，越写越完整、越写越准确。',
                },
              ].map((item) => (
                <div key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-950">{item.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="text-base font-semibold text-foreground-950">站点信息</h2>
              <ul className="mt-4 space-y-3 text-sm text-foreground-600">
                <li className="flex items-center gap-3">
                  <i className="ri-store-2-line text-foreground-500" aria-hidden="true"></i>
                  {siteConfig.name}
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-archive-line text-foreground-500" aria-hidden="true"></i>
                  设定科普站
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-mail-line text-foreground-500" aria-hidden="true"></i>
                  <a href={`mailto:${siteConfig.email}`} className="transition-colors hover:text-primary-700">{siteConfig.email}</a>
                </li>
              </ul>
              <Link
                to="/lore"
                className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                浏览设定文章
                <i className="ri-arrow-right-line text-sm" aria-hidden="true"></i>
              </Link>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="text-base font-semibold text-foreground-950">快速导航</h2>
              <ul className="mt-4 space-y-3 text-sm">
                <li><Link to="/" className="text-foreground-600 transition-colors hover:text-primary-700">首页</Link></li>
                <li><Link to="/lore" className="text-foreground-600 transition-colors hover:text-primary-700">设定与世界观</Link></li>
                <li><Link to="/faq" className="text-foreground-600 transition-colors hover:text-primary-700">常见问题</Link></li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}